import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { normaliseForSearch } from '../../utils/diacritics';
import QuickAddChips from './QuickAddChips';
import DisplayItemsEditor from './DisplayItemsEditor';
import './DictionaryEntryForm.css';

/**
 * DictionaryEntryForm — single-flow entry creation.
 *
 * Layout:
 *   1. Canonical form field (pre-filled, editable)
 *   2. QuickAddChips (staging mode — items go to local state)
 *   3. Staged DisplayItems list (with delete)
 *   4. "+ Add annotation" button → opens DisplayItemsEditor (staging mode)
 *   5. "Create Entry" / "Cancel" buttons
 *
 * Props:
 *   initialCanonicalForm — pre-fill from search term
 *   onSaved(entry) — called after successful Firestore create
 *   onCancel() — called when user cancels
 */
function DictionaryEntryForm({ initialCanonicalForm = '', onSaved, onCancel }) {
  const [canonicalForm, setCanonicalForm] = useState(initialCanonicalForm);
  const [stagedItems, setStagedItems] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canCreate = canonicalForm.trim().length > 0;

  const handleCreate = async () => {
    if (!canCreate) return;
    setSaving(true);
    setError('');

    try {
      const entry = {
        canonicalForm: canonicalForm.trim(),
        canonicalFormNormalised: normaliseForSearch(canonicalForm),
        language: 'fr',
        annotationCount: 0,
        displayItems: stagedItems,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        synonymIds: [],
        antonymIds: [],
        registerVariantIds: [],
        expressionIds: [],
        aliases: [],
      };
      const docRef = await addDoc(collection(db, 'dictionary'), entry);
      onSaved({ id: docRef.id, ...entry });
    } catch (e) {
      console.error('Error creating entry:', e);
      setError('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    if (stagedItems.length > 0) {
      setShowCancelConfirm(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmDiscard = () => {
    setShowCancelConfirm(false);
    setStagedItems([]);
    onCancel();
  };

  const handleKeepEditing = () => {
    setShowCancelConfirm(false);
  };

  const handleItemStaged = (item) => {
    setStagedItems((prev) => [...prev, item]);
  };

  const handleRemoveStagedItem = (index) => {
    setStagedItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditorSaved = (item) => {
    setStagedItems((prev) => [...prev, item]);
    setShowEditor(false);
  };

  const getItemDisplay = (item) => {
    if (item.type === 'text') {
      return { key: item.key, value: item.value };
    }
    if (item.type === 'composition') {
      const labels = (item.components || []).map((c) => c.label).join(' + ');
      return { key: item.key, value: labels };
    }
    return { key: '?', value: JSON.stringify(item) };
  };

  return (
    <div className="entry-form">
      <h3 className="entry-form-title">Add new dictionary entry</h3>

      {/* 1. Canonical form field */}
      <div className="entry-form-field">
        <label className="entry-form-label">Word / Expression *</label>
        <input
          className="entry-form-input"
          type="text"
          value={canonicalForm}
          onChange={(e) => setCanonicalForm(e.target.value)}
          placeholder="e.g. être, se rendre compte, ouais"
          autoFocus
        />
      </div>

      {/* 2. QuickAddChips in staging mode */}
      <QuickAddChips
        onItemStaged={handleItemStaged}
      />

      {/* 3. Staged items list */}
      {stagedItems.length > 0 && (
        <div className="entry-form-staged-items">
          <p className="entry-form-staged-label">Annotations:</p>
          {stagedItems.map((item, index) => {
            const display = getItemDisplay(item);
            return (
              <div key={index} className="entry-form-staged-item">
                <div className="arrow-card-pair">
                  <div className="arrow-card arrow-card--key">
                    <span className="arrow-card-text">{display.key}</span>
                  </div>
                  {display.value && (
                    <div className="arrow-card arrow-card--value">
                      <span className="arrow-card-text">{display.value}</span>
                    </div>
                  )}
                </div>
                <button
                  className="entry-form-staged-remove"
                  onClick={() => handleRemoveStagedItem(index)}
                  aria-label="Remove staged annotation"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* 4. "+ Add annotation" button / DisplayItemsEditor */}
      {!showEditor ? (
        <button
          className="entry-form-freeform-btn"
          onClick={() => setShowEditor(true)}
        >
          + Add annotation
        </button>
      ) : (
        <DisplayItemsEditor
          stagingMode
          onSaved={handleEditorSaved}
          onCancel={() => setShowEditor(false)}
        />
      )}

      {/* Error message */}
      {error && <div className="entry-form-error">{error}</div>}

      {/* 5. Create / Cancel buttons */}
      <div className="entry-form-actions">
        <button
          className="entry-form-save-btn"
          onClick={handleCreate}
          disabled={!canCreate || saving}
        >
          {saving ? 'Creating…' : 'Create Entry'}
        </button>
        {onCancel && (
          <button className="entry-form-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
        )}
      </div>

      {/* Cancel confirmation dialog */}
      {showCancelConfirm && (
        <div className="entry-form-confirm-overlay">
          <div className="entry-form-confirm-dialog">
            <p className="entry-form-confirm-text">
              You have unsaved annotations. Discard and cancel?
            </p>
            <div className="entry-form-confirm-actions">
              <button
                className="entry-form-confirm-discard"
                onClick={handleConfirmDiscard}
              >
                Discard
              </button>
              <button
                className="entry-form-confirm-keep"
                onClick={handleKeepEditing}
              >
                Keep editing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DictionaryEntryForm;
